import { formatSalesOrderNumber } from '@zspace/format';
import { DevicePermissions } from '@zspace/roles';
import {
  BasicDeviceGroupData,
  DeferredResponse,
  DeviceAssignmentsCriteria,
  DeviceAssignmentsStatus,
  DeviceAssignmentsTableRow,
  DeviceData,
  MoveDevicesData,
  PaginatedAPIResponse,
  PartialBy,
  SortDirection,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element, Tabs } from 'react-bulma-components';
import {
  FaBolt,
  FaClockRotateLeft,
  FaDesktop,
  FaFileLines,
  FaFolderOpen,
  FaLaptop,
  FaUser,
} from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom';
import {
  fetchAllAvailableDeviceGroups,
  moveDevicesToDeviceGroup,
} from '../../device-groups/device-groups-service';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsZspaceInternalUser from '../../shared/hooks/is-zspace-internal-user';
import useSalesOrderPermissions from '../../shared/hooks/sales-order-permissions';
import useToast from '../../shared/hooks/toasts';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { formatDateToLocaleString } from '../../shared/utils';
import BoxLayout from '../../ui/box-layout/box-layout';
import DetailsCard from '../../ui/details-card/details-card';
import InboxTable from '../../ui/inbox-table/inbox-table';
import InfoLabel from '../../ui/info-label/info-label';
import Link from '../../ui/link/link';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import DeviceDetailsAssignmentsTable from '../device-details-assignments-table/device-details-assignments-table';
import { fetchDeviceAssignments, fetchDeviceById } from '../devices-service';
import MoveDevicesModal from '../move-devices-modal/move-devices-modal';
import styles from './device-details-page.module.scss';

const SUCCESSFULLY_MOVED_DEVICE_MESSAGE = 'Device successfully moved';

const initialCriteria: DeviceAssignmentsCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'softwareSeat.licensingProductGroup.displayName',
  sortDirection: SortDirection.ASC,
  status: DeviceAssignmentsStatus.CURRENT,
};

export const loader: LoaderFunction = async ({ params }) => {
  const id = params.id;
  const device = fetchDeviceById(id!);
  const deviceAssignments = fetchDeviceAssignments(id!, initialCriteria);
  const allAvailableDeviceGroups = fetchAllAvailableDeviceGroups();

  const response = Promise.all([
    device,
    deviceAssignments,
    allAvailableDeviceGroups,
  ]);

  return defer({ response });
};

export function DeviceDetailsPageContent() {
  const [device, deviceAssignments, allAvailableDeviceGroups] =
    useAsyncValue() as [
      DeviceData,
      PaginatedAPIResponse<DeviceAssignmentsTableRow>,
      BasicDeviceGroupData[]
    ];
  const toast = useToast();
  const revalidator = useRevalidator();
  const { userHasSalesOrderManagePermissions } = useSalesOrderPermissions();
  const isZspaceInternalUser = useIsZspaceInternalUser();
  const { executeHttpRequest: executeFetchMoreAssignments } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchAssignments,
    isLoading: isLoadingAssignments,
  } = useHttpRequest();
  const [isMoveDevicesModalVisible, setIsMoveDevicesModalVisible] =
    useState(false);
  const [criteriaFilter, setCriteriaFilter] =
    useState<DeviceAssignmentsCriteria>({
      ...initialCriteria,
    });
  const [assignments, setAssignments] =
    useState<PaginatedAPIResponse<DeviceAssignmentsTableRow>>(
      deviceAssignments
    );
  const [hasMore, setHasMore] = useState<boolean>(!!assignments.hasMore);

  const deviceDetailsPageTabs = useMemo(
    () =>
      isZspaceInternalUser && device.hasOriginalConfiguration
        ? Object.values(DeviceAssignmentsStatus)
        : [DeviceAssignmentsStatus.CURRENT],
    [device.hasOriginalConfiguration, isZspaceInternalUser]
  );

  const pageTabLabel = (tab: DeviceAssignmentsStatus) => {
    switch (tab) {
      case DeviceAssignmentsStatus.CURRENT:
        return 'Current assignments';
      case DeviceAssignmentsStatus.ORIGINAL:
        return 'Original configuration';
    }
  };

  const canManageDevice = useMemo(() => {
    return userHasSalesOrderManagePermissions(device.salesOrder.id);
  }, [device.salesOrder.id, userHasSalesOrderManagePermissions]);

  const tableConfig = useMemo(() => {
    const { search, sortBy, sortDirection } = criteriaFilter;
    return {
      search,
      sortBy,
      sortDirection,
    };
  }, [criteriaFilter]);

  const fetchAssignments = useCallback(
    async (criteria: DeviceAssignmentsCriteria) => {
      executeFetchAssignments({
        asyncFunction: async () => {
          const newAssignments = await fetchDeviceAssignments(
            device.id,
            criteria
          );

          setAssignments(newAssignments);
          setHasMore(newAssignments.hasMore!);
        },
      });
    },
    [device.id, executeFetchAssignments]
  );

  const fetchMoreAssignments = useCallback(
    () =>
      executeFetchMoreAssignments({
        asyncFunction: async () => {
          const nextPage = criteriaFilter.pageNumber + 1;
          const updatedCriteria = {
            ...criteriaFilter,
            pageNumber: nextPage,
          };
          const newAssignments = await fetchDeviceAssignments(
            device.id,
            updatedCriteria
          );
          setAssignments((prevAssignments) => ({
            ...prevAssignments,
            data: [...prevAssignments.data, ...newAssignments.data],
          }));
          setCriteriaFilter(updatedCriteria);
          setHasMore(newAssignments.hasMore!);
        },
      }),
    [criteriaFilter, device.id, executeFetchMoreAssignments]
  );

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<DeviceAssignmentsTableRow, unknown>): void => {
      const { itemsPerPage, pageNumber } = initialCriteria;
      const newCriteria = {
        ...criteriaFilter,
        ...value.config,
        itemsPerPage,
        pageNumber,
        sortBy: value.column?.key || criteriaFilter.sortBy,
      };

      setCriteriaFilter(newCriteria);
      fetchAssignments(newCriteria);
    },
    [criteriaFilter, fetchAssignments]
  );

  const isActiveTab = useCallback(
    (tab: DeviceAssignmentsStatus) => tab === criteriaFilter.status,
    [criteriaFilter.status]
  );

  const changeTab = useCallback(
    (tab: DeviceAssignmentsStatus) => () => {
      const newCriteria = {
        ...initialCriteria,
        status: tab,
        sortBy:
          tab === DeviceAssignmentsStatus.CURRENT
            ? 'softwareSeat.licensingProductGroup.displayName'
            : 'softwareAssignment.softwareSeat.licensingProductGroup.displayName',
      };
      setCriteriaFilter(newCriteria);
      fetchAssignments(newCriteria);
    },
    [fetchAssignments]
  );

  const onMoveDeviceToDeviceGroup = useCallback(
    async (moveDevicesData: PartialBy<MoveDevicesData, 'devicesIds'>) => {
      await moveDevicesToDeviceGroup({
        ...moveDevicesData,
        devicesIds: [device.id],
      });
      setIsMoveDevicesModalVisible(false);
      revalidator.revalidate();
      toast.success(SUCCESSFULLY_MOVED_DEVICE_MESSAGE);
    },
    [device.id, revalidator, toast]
  );

  return (
    <>
      <BoxLayout
        className="is-min-height-80 mx-10 my-4"
        header={
          <Element className="p-4">
            <BackButton />
            <h1 className="is-size-3 has-text-weight-light">Device details</h1>
          </Element>
        }
      >
        <Columns className="p-4">
          <Columns display="flex" className={styles.basicInformationContainer}>
            <Columns.Column display="flex" className={styles.informationColumn}>
              <DetailsCard title="Basic information">
                <InfoLabel
                  icon={<FaBolt />}
                  label="Serial number"
                  content={<span>{device.serialNumber?.number || '-'}</span>}
                />
                <InfoLabel
                  icon={<FaDesktop />}
                  label="Device name"
                  content={
                    <Conditional condition={!!device.name}>
                      <Conditional.True>
                        <span>{device.name}</span>
                      </Conditional.True>
                      <Conditional.False>
                        <span>{device.defaultName}</span>
                      </Conditional.False>
                    </Conditional>
                  }
                />
                <InfoLabel
                  icon={<FaLaptop />}
                  label="Device type"
                  content={<span>{device.model.displayName}</span>}
                />
                <InfoLabel
                  icon={<FaFolderOpen />}
                  label="Device group"
                  content={
                    <Conditional condition={!!device.deviceGroup}>
                      <Conditional.True>
                        <span>{device.deviceGroup?.name}</span>
                      </Conditional.True>
                      <Conditional.False>
                        <span>Ungrouped</span>
                        <If condition={canManageDevice}>
                          <span
                            className="has-text-primary-dark is-underlined is-clickable"
                            onClick={() => setIsMoveDevicesModalVisible(true)}
                          >
                            Move to group
                          </span>
                        </If>
                      </Conditional.False>
                    </Conditional>
                  }
                />
                <InfoLabel
                  icon={<FaFileLines />}
                  label="Sales Order"
                  content={
                    <Link
                      to={`/sales-orders/${device.salesOrder.id}`}
                      underlined
                    >
                      {formatSalesOrderNumber(device.salesOrder.number)}
                    </Link>
                  }
                />
                <InfoLabel
                  icon={<FaUser />}
                  label="End User Software Contact"
                  content={<span>{device.salesOrder.endUser || ''}</span>}
                />
                <InfoLabel
                  icon={<FaClockRotateLeft />}
                  label="Last modified date"
                  content={
                    <span>
                      {device.lastModifiedAt
                        ? formatDateToLocaleString(device.lastModifiedAt)
                        : ''}
                    </span>
                  }
                />
              </DetailsCard>
            </Columns.Column>
          </Columns>
          <Columns
            display="flex"
            flexDirection="column"
            className={`is-flex-grow-1 ${styles.tabsColumn}`}
          >
            <Columns.Column>
              <Tabs>
                {deviceDetailsPageTabs.map((tab) => (
                  <Tabs.Tab
                    key={tab}
                    active={isActiveTab(tab)}
                    onClick={changeTab(tab)}
                  >
                    {pageTabLabel(tab)}
                  </Tabs.Tab>
                ))}
              </Tabs>
              <Conditional condition={isLoadingAssignments}>
                <Conditional.True>
                  <PageSpinner />
                </Conditional.True>
                <Conditional.False>
                  <InboxTable
                    table={
                      <DeviceDetailsAssignmentsTable
                        viewMode={criteriaFilter.status!}
                        fetchMore={fetchMoreAssignments}
                        hasMore={!!hasMore}
                        onChange={handleOnTableChange}
                        dataSource={assignments.data}
                        deviceSerialNumber={device.serialNumber}
                        rowKey={'id'}
                        config={tableConfig}
                      />
                    }
                    isTableEmpty={assignments.data.length === 0}
                    config={tableConfig}
                    enableSearch={false}
                  />
                </Conditional.False>
              </Conditional>
            </Columns.Column>
          </Columns>
        </Columns>
      </BoxLayout>
      <MoveDevicesModal
        show={isMoveDevicesModalVisible}
        selectedDevices={[device]}
        deviceGroups={allAvailableDeviceGroups}
        onMoveDevicesToDeviceGroup={onMoveDeviceToDeviceGroup}
        onCancel={() => setIsMoveDevicesModalVisible(false)}
      />
    </>
  );
}

export function DeviceDetailsPage() {
  const { response } = useLoaderData() as DeferredResponse<DeviceData>;

  return (
    <ProtectedPage permissions={DevicePermissions.DEVICE_DETAILS_READ}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <DeviceDetailsPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default DeviceDetailsPage;
